// src/pages/admin/AdminFotos.js
import React, { useState } from "react";
import styled from "styled-components";
import AdminLayout from "../../components/admin/AdminLayout";

const API_URL = process.env.REACT_APP_API_URL || "https://crm-backend-5tk6.onrender.com";

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  max-width: 600px;
`;

const DropZone = styled.div`
  border: 2px dashed ${p => p.$drag ? "#c8102e" : "#ddd"};
  border-radius: 10px;
  padding: 40px;
  text-align: center;
  color: #888;
  cursor: pointer;
  background: ${p => p.$drag ? "#fff5f5" : "#fafafa"};
  transition: all 0.2s;
  margin: 16px 0;
  p { margin: 8px 0 0; font-size: 0.82rem; }
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 16px 0;
  img { width: 80px; height: 80px; object-fit: cover; border-radius: 8px; border: 1px solid #eee; }
`;

const Info = styled.div`
  p { margin: 0 0 4px; font-size: 0.85rem; color: #333; }
  span { font-size: 0.8rem; color: #888; }
`;

const Btn = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.$secondary ? "#fff" : "#c8102e"};
  color: ${p => p.$secondary ? "#444" : "#fff"};
  border: ${p => p.$secondary ? "1px solid #ddd" : "none"};
  margin-right: 8px;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-top: 12px;
  background: ${p => p.error ? "#fdecea" : "#e8f5e9"};
  color: ${p => p.error ? "#c8102e" : "#2e7d32"};
`;

const CURP_REGEX = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

export default function AdminFotos() {
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [drag, setDrag] = useState(false);
  const [validacion, setValidacion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const token = localStorage.getItem("admin_token");

  const procesarArchivo = (file) => {
    if (!file) return;
    setResultado(null);

    // Validar extensión
    if (!file.name.toLowerCase().endsWith(".png")) {
      setValidacion({ ok: false, mensaje: "El archivo debe ser PNG." });
      return;
    }

    // Extraer CURP del nombre (sin extensión)
    const curp = file.name.replace(/\.png$/i, "").toUpperCase();
    if (!CURP_REGEX.test(curp)) {
      setValidacion({ ok: false, mensaje: `"${curp}" no tiene formato de CURP válido (18 caracteres).` });
      return;
    }

    setArchivo(file);
    setPreview(URL.createObjectURL(file));
    setValidacion({ ok: true, curp, mensaje: `CURP válida: ${curp}` });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    procesarArchivo(e.dataTransfer.files[0]);
  };

  const verificarEnBD = async () => {
    if (!validacion?.curp) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/buscar-certificados?curp=${validacion.curp}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.encontrado) {
        setValidacion(v => ({ ...v, enBD: true, nombre: data.usuario?.nombre_completo }));
      } else {
        setValidacion(v => ({ ...v, enBD: false }));
      }
    } catch {
      setValidacion(v => ({ ...v, enBD: false }));
    } finally {
      setLoading(false);
    }
  };

  const subir = async () => {
    if (!archivo || !validacion?.ok) return;
    setLoading(true);
    setResultado(null);
    try {
      const formData = new FormData();
      formData.append("foto", archivo);
      formData.append("curp", validacion.curp);

      const res = await fetch(`${API_URL}/api/admin/subir-foto`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setResultado({ ok: true, mensaje: "Foto subida correctamente." });
        setArchivo(null);
        setPreview(null);
        setValidacion(null);
      } else {
        setResultado({ ok: false, mensaje: data.error || "Error al subir." });
      }
    } catch {
      setResultado({ ok: false, mensaje: "Error de conexión." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout titulo="Carga de fotos (imgcurp)">
      <Card>
        <p style={{ margin: "0 0 4px", fontSize: "0.85rem", color: "#555" }}>
          El nombre del archivo debe ser la <strong>CURP del participante</strong> en formato PNG.
        </p>
        <p style={{ margin: "0 0 16px", fontSize: "0.82rem", color: "#aaa" }}>
          Ejemplo: <code>MARI851102MTSRMS03.png</code>
        </p>

        <DropZone
          $drag={drag}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => document.getElementById("fotoInput").click()}
        >
          🖼️ Arrastra la imagen aquí o haz clic para seleccionar
          <p>Solo archivos .PNG</p>
        </DropZone>
        <input id="fotoInput" type="file" accept=".png" style={{ display: "none" }}
          onChange={e => procesarArchivo(e.target.files[0])} />

        {validacion && (
          <Alert error={!validacion.ok}>
            {validacion.ok ? "✅" : "❌"} {validacion.mensaje}
            {validacion.enBD === true && ` — ${validacion.nombre}`}
            {validacion.enBD === false && " — ⚠️ CURP no encontrada en la base de datos."}
          </Alert>
        )}

        {preview && validacion?.ok && (
          <Preview>
            <img src={preview} alt="preview" />
            <Info>
              <p><strong>{archivo?.name}</strong></p>
              <span>{(archivo?.size / 1024).toFixed(1)} KB</span>
            </Info>
          </Preview>
        )}

        {archivo && validacion?.ok && (
          <div style={{ marginTop: "12px" }}>
            {validacion.enBD === undefined && (
              <Btn $secondary onClick={verificarEnBD} disabled={loading}>
                {loading ? "Verificando..." : "🔍 Verificar CURP en BD"}
              </Btn>
            )}
            {validacion.enBD === true && (
              <Btn onClick={subir} disabled={loading}>
                {loading ? "Subiendo..." : "⬆️ Subir foto"}
              </Btn>
            )}
            <Btn $secondary onClick={() => { setArchivo(null); setPreview(null); setValidacion(null); }}>
              Cancelar
            </Btn>
          </div>
        )}

        {resultado && (
          <Alert error={!resultado.ok} style={{ marginTop: "16px" }}>
            {resultado.ok ? "✅" : "❌"} {resultado.mensaje}
          </Alert>
        )}
      </Card>
    </AdminLayout>
  );
}